import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const ACCESS_KEY = "auth.access_token";
const REFRESH_KEY = "auth.refresh_token";
const USER_KEY = "auth.user";

export const tokenStore = {
  getAccess: () => localStorage.getItem(ACCESS_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_KEY),
  set: (access, refresh) => {
    if (access) localStorage.setItem(ACCESS_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  },
  // Last known profile, so the installed app can start while offline.
  setUser: (u) => localStorage.setItem(USER_KEY, JSON.stringify(u)),
  getUser: () => {
    try { return JSON.parse(localStorage.getItem(USER_KEY)); } catch { return null; }
  },
  clear: () => {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
  },
};

export const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  const access = tokenStore.getAccess();
  if (access && !config.headers.Authorization) {
    config.headers.Authorization = "Bearer " + access;
  }
  return config;
});

// Refresh-on-401 with rotation
let refreshPromise = null;

async function refreshTokens() {
  const refresh_token = tokenStore.getRefresh();
  if (!refresh_token) throw new Error("No refresh token");
  const { data } = await axios.post(`${API_BASE}/auth/refresh`, {
    refresh_token,
  });
  tokenStore.set(data.access_token, data.refresh_token);
  return data.access_token;
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const url = original?.url || "";
    const isAuthRoute = url.includes("/auth/login") || url.includes("/auth/register") || url.includes("/auth/refresh");
    if (status === 401 && !original._retry && !isAuthRoute && tokenStore.getRefresh()) {
      original._retry = true;
      try {
        refreshPromise = refreshPromise || refreshTokens();
        const newAccess = await refreshPromise;
        refreshPromise = null;
        original.headers.Authorization = "Bearer " + newAccess;
        return api(original);
      } catch (e) {
        refreshPromise = null;
        tokenStore.clear();
        // Force a redirect to /login
        if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
          window.location.href = "/login";
        }
        return Promise.reject(e);
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Sprint 10 — Idempotency support for mutating Order operations.
 *
 * Usage:
 *   await api.post("/orders/.../confirm", payload, withIdempotencyKey());
 *   await api.post("/orders/.../confirm", payload, withIdempotencyKey("custom-key"));
 *
 * Returns an axios request config with the `Idempotency-Key` header set.
 * The server deduplicates by this key so retries from offline clients
 * (Flutter outbox, lost connection, etc.) do not double-apply.
 */
export function newIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function withIdempotencyKey(key, extraConfig = {}) {
  const idemKey = key || newIdempotencyKey();
  return {
    ...extraConfig,
    headers: {
      ...(extraConfig.headers || {}),
      "Idempotency-Key": idemKey,
    },
  };
}

export function formatApiError(err) {
  const d = err?.response?.data;
  if (!d) return err?.message || "Network error. Please try again.";
  if (typeof d === "string") return d;
  if (typeof d.message === "string") return d.message;
  const detail = d.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  }
  if (detail && typeof detail.message === "string") return detail.message;
  return "Something went wrong. Please try again.";
}
